/**
 * <hy-sticky-atc>: Sticky-Kaufleiste für snippets/hy-product-sticky-atc.liquid.
 * Erscheint, sobald der Sentinel direkt nach dem regulären Kaufformular
 * den sichtbaren Bereich verlässt (IntersectionObserver) — kein
 * gemessenes Rechteck, siehe Plan Abschnitt 3. Ein Klick auf den eigenen
 * Button delegiert an den echten Kaufbutton im Hauptformular, statt eine
 * zweite Absende-Logik nachzubauen. hy:variant:changed hält Preis und
 * Kaufbarkeit mit dem Hauptformular synchron, das zu diesem Zeitpunkt
 * bereits aktualisiert ist (das Event feuert erst NACH dem DOM-Tausch in
 * <hy-variant-picker>).
 */
class HyStickyAtc extends HTMLElement {
  #controller = null;
  #observer = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const sectionId = this.dataset.sectionId;

    // Follows the main buy area's primary action: when the quote request is
    // the primary button (hy-product-buy, primary_action "inquiry"), the bar
    // shows and triggers that request instead of add-to-cart. Delegating the
    // click keeps one source of truth (dialog or plain link).
    const button = this.querySelector('.hy-sticky-atc__button');
    const primaryInquiry = this.#primaryInquiry(sectionId);
    if (button && primaryInquiry) {
      // A quote request is not a purchase: no cart icon.
      button.querySelector('.hy-sticky-atc__icon')?.remove();
      button.querySelector('.hy-button__icon')?.remove();
      button.classList.remove('hy-button--with-icon');
      this.#label(button).textContent = primaryInquiry.textContent.trim();
      button.disabled = false;
    }

    button?.addEventListener(
      'click',
      () => {
        const inquiry = this.#primaryInquiry(sectionId);
        if (inquiry) {
          inquiry.click();
          return;
        }
        this.#root(sectionId)
          ?.querySelector('[data-hy-product-form] button[name="add"]')
          ?.click();
      },
      { signal },
    );

    document.addEventListener(
      'hy:variant:changed',
      (event) => {
        if (event.detail?.sectionId === sectionId) this.#syncFromMainForm(sectionId);
      },
      { signal },
    );

    // Der Sentinel sitzt seit AP-232 im Kaufbereich-Block, direkt hinter dem
    // Formular — dadurch immer exakt unter dem echten Kaufbutton, egal wohin
    // der Merchant den Block zieht. Bewusst AUSSERHALB des getauschten
    // Formular-Fragments: ein ersetzter Knoten würde den
    // IntersectionObserver stillschweigend abmelden.
    const sentinel = this.#root(sectionId)?.querySelector('[data-hy-atc-sentinel]');
    if (sentinel) {
      // isIntersecting allein unterscheidet nicht die Richtung: der Sentinel
      // ist beim ersten Paint auf kurzen Viewports oft schon unterhalb des
      // sichtbaren Bereichs, obwohl niemand gescrollt hat — die Leiste soll
      // aber nur erscheinen, wenn er nach OBEN aus dem Bereich gewandert ist
      // (tatsächlich vorbeigescrollt), nicht weil er noch nicht erreicht wurde.
      // Am Seitenende wieder einfahren (Design-Review Runde 6, P8). Die Leiste
      // blieb bis zum letzten Bildpunkt stehen und verdeckte dauerhaft die
      // unterste Fusszeilenreihe — man konnte nicht weiterscrollen, um sie
      // freizulegen. Wer unten angekommen ist, hat den Kaufbereich ohnehin
      // hinter sich; dort trägt die Leiste nichts mehr bei.
      const amSeitenende = () => {
        const rest = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
        return rest < this.offsetHeight;
      };

      const auswerten = (top, imBild) => {
        this.toggleAttribute('data-hy-visible', !imBild && top < 0 && !amSeitenende());
      };

      this.#observer = new IntersectionObserver(([entry]) => {
        auswerten(entry.boundingClientRect.top, entry.isIntersecting);
      });
      this.#observer.observe(sentinel);

      // WARUM ES DEN BEOBACHTER ALLEIN NICHT TUT — gemessen, nicht vermutet:
      //
      // Auf dem Desktop erschien die Leiste korrekt ab rund 1.500 px
      // Scrollweg. Auf dem Handy NIE, obwohl der Sentinel dort längst 400 bis
      // 1.900 px über der Fensterkante lag. Mobil ist aber genau der Fall,
      // für den eine mitlaufende Kaufleiste gebaut wird: Der Kaufbereich ist
      // nach einem Bildschirm vorbei, danach folgen zehn weitere.
      //
      // Der Grund ist die Ausgangslage. Auf dem Desktop steht der Sentinel
      // beim Laden IM Bild; beim Scrollen verlässt er es nach oben, und genau
      // dieser Übergang meldet sich. Auf einem kurzen Fenster steht er von
      // Anfang an DARUNTER — er wechselt beim Scrollen nur die Seite, ohne je
      // durchs Bild zu laufen. `isIntersecting` bleibt durchgehend falsch,
      // der Zustand ändert sich für den Beobachter also nie, und er meldet
      // sich kein einziges Mal.
      //
      // Ein zusätzlicher Scroll-Horcher schließt die Lücke. Er misst nur eine
      // Rechteckposition, läuft gedrosselt auf einen Frame und ist passiv —
      // er blockiert das Scrollen also nicht. Der Beobachter bleibt, weil er
      // den Normalfall ohne jeden Frame erledigt.
      let angefordert = false;
      const beiBewegung = () => {
        if (angefordert) return;
        angefordert = true;
        requestAnimationFrame(() => {
          angefordert = false;
          const rect = sentinel.getBoundingClientRect();
          auswerten(rect.top, rect.bottom > 0 && rect.top < window.innerHeight);
        });
      };

      window.addEventListener('scroll', beiBewegung, { passive: true, signal });
      window.addEventListener('resize', beiBewegung, { passive: true, signal });
      beiBewegung();
    }

    // Erst jetzt, da die Leiste ohne eine per JS verfolgte
    // Bildlaufposition kein eigenständiges Steuerelement wäre.
    this.setAttribute('data-hy-atc-ready', '');
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  /**
   * Bewusst bei jedem Zugriff neu aufgelöst statt in connectedCallback
   * gemerkt: <hy-variant-picker> ersetzt bei einem Combined-Listing-Wechsel
   * den ganzen Wurzelknoten, eine gemerkte Referenz zeigte danach auf einen
   * abgehängten Teilbaum.
   */
  #root(sectionId) {
    return document.getElementById(`HyProduct-${sectionId}`);
  }

  #primaryInquiry(sectionId) {
    return this.#root(sectionId)?.querySelector('.hy-main-product__inquiry--primary') ?? null;
  }

  #syncFromMainForm(sectionId) {
    const root = this.#root(sectionId);
    const mainPrice = root?.querySelector('[data-hy-price]');
    const mainButton = root?.querySelector('[data-hy-product-form] button[name="add"]');
    if (!mainPrice || !mainButton) return;

    const priceValue = mainPrice.querySelector('.hy-main-product__price-value')?.textContent ?? '';
    const priceEl = this.querySelector('.hy-sticky-atc__price');
    const valueEl = priceEl?.querySelector('.hy-price__value');
    // Only the amount is replaced; the net line below it keeps its markup and
    // takes the main price's net line. After a variant change both wordings
    // carry that (long) text: a correct amount matters more than the short
    // phone wording, which only exists in the server render.
    if (valueEl) valueEl.textContent = priceValue;
    else if (priceEl) priceEl.textContent = priceValue;

    // The long wording where the main price carries both (short_legal).
    const mainNetEl = mainPrice.querySelector('.hy-price__net');
    const mainNet = (mainNetEl?.querySelector('.hy-price__long') ?? mainNetEl)?.textContent.trim();
    const netEl = priceEl?.querySelector('.hy-price__net');
    if (netEl && !mainNet) netEl.style.display = 'none'; // [hidden] loses to .hy-price__tax { display: block }
    if (netEl && mainNet) {
      netEl.style.removeProperty('display');
      netEl.querySelectorAll('.hy-price__long, .hy-price__short').forEach((el) => {
        el.textContent = mainNet;
      });
    }

    // Availability line follows the main button (disabled = not purchasable).
    const status = this.querySelector('.hy-sticky-atc__status');
    if (status) status.style.display = mainButton.disabled ? 'none' : '';

    const button = this.querySelector('.hy-sticky-atc__button');
    // A quote request stays available whatever the variant's stock.
    if (button && !this.#primaryInquiry(sectionId)) {
      button.disabled = mainButton.disabled;
      const mainLabel = mainButton.querySelector('.hy-button__label')?.textContent;
      if (mainLabel) this.#label(button).textContent = mainLabel;
    }
  }

  /** The text part of the bar's button; the button itself if the markup is older. */
  #label(button) {
    return button.querySelector('.hy-sticky-atc__label') ?? button;
  }
}

customElements.define('hy-sticky-atc', HyStickyAtc);
